import { ITabsPanelStates, ITabsPanelCommonSemanticColors } from './types';
import { IThemeSemantics } from '../../../styling';
import { createThemeCompiler } from '../../../utils';

interface ITabsPanelStyleBuilderVariables extends Partial<ITabsPanelCommonSemanticColors> {
    // Default
    tabItemText?: string;
    tabItemBorder?: string;
    tabItemBackground?: string;
    tabSeperator?: string;

    // Hovered
    hoveredTabItemText?: string;
    hoveredTabItemBorder?: string;
    hoveredTabItemBackground?: string;
    hoveredTabSeperator?: string;
    hoveredTabHeaderWrapperBackground?: string;
    hoveredTabHeaderWrapperBorder?: string;

    // Disabled
    disabledTabItemText?: string;
    disabledTabItemBorder?: string;
    disabledTabItemBackground?: string;
    disabledTabSeperator?: string;
    disabledTabHeaderWrapperBackground?: string;
    disabledTabHeaderWrapperBorder?: string;

    // Active
    activeTabItemText?: string;
    activeTabItemBorder?: string;
    activeTabContentBorder?: string;
    activeTabItemBackground?: string;
    activeTabSeperator?: string;
    activeTabHeaderWrapperBackground?: string;
    activeTabHeaderWrapperBorder?: string;

    // Focussed
    focussedTabItemText?: string;
    focussedTabItemBorder?: string;
    focussedTabItemBackground?: string;
    focussedTabSeperator?: string;
    focussedTabHeaderWrapperBackground?: string;
    focussedTabHeaderWrapperBorder?: string;
}

const buildTabsPanelTheme = (variables: ITabsPanelStyleBuilderVariables, { palette }: IThemeSemantics): ITabsPanelStates => {
    const {
        // Default
        tabItemText = palette.neutralPrimary,
        tabItemBorder = palette.neutralQuaternary,
        tabItemBackground = palette.transparent,
        tabSeperator = palette.transparent,
        tabContentBorder = palette.neutralQuaternary,
        tabHeaderWrapperBackground = palette.transparent,
        tabHeaderWrapperBorder = palette.transparent,

        // Active
        activeTabItemText = palette.white,
        activeTabItemBorder = palette.themePrimary,
        activeTabItemBackground = palette.themePrimary,
        activeTabSeperator = palette.themePrimary,

        // Focussed
        focussedTabItemText = palette.neutralPrimary,
        focussedTabItemBorder = palette.themePrimary,
        focussedTabItemBackground = palette.themeLight,
        focussedTabSeperator = palette.themePrimary,

        // Hovered
        hoveredTabItemText = palette.neutralPrimary,
        hoveredTabItemBorder = palette.themePrimary,
        hoveredTabItemBackground = palette.themeLight,
        hoveredTabSeperator = palette.themePrimary,

        // Disabled
        disabledTabItemText = '#667287',
        disabledTabItemBorder = '#F8F9FA',
        disabledTabItemBackground = '#F8F9FA',
        disabledTabSeperator = palette.transparent

    } = variables;

    return {
        tabContentBorder,
        tabHeaderWrapperBorder,
        tabHeaderWrapperBackground,
        default: {
            tabItemBackground,
            tabItemBorder,
            tabItemText,
            tabSeperator
        },
        active: {
            tabItemBackground: activeTabItemBackground,
            tabItemBorder: activeTabItemBorder,
            tabItemText: activeTabItemText,
            tabSeperator: activeTabSeperator
        },
        disabled: {
            tabItemBackground: disabledTabItemBackground,
            tabItemBorder: disabledTabItemBorder,
            tabItemText: disabledTabItemText,
            tabSeperator: disabledTabSeperator
        },
        focussed: {
            tabItemBackground: focussedTabItemBackground,
            tabItemBorder: focussedTabItemBorder,
            tabItemText: focussedTabItemText,
            tabSeperator: focussedTabSeperator
        },
        hovered: {
            tabItemBackground: hoveredTabItemText,
            tabItemBorder: hoveredTabItemBorder,
            tabItemText: hoveredTabItemBackground,
            tabSeperator: hoveredTabSeperator
        }
    };
};

export const tabsPanelThemeCompiler = createThemeCompiler((semantics) => {
    const { borderRadius, palette, gap, stroke } = semantics;
    return {
        tabsPanelConventional: {
            colors: buildTabsPanelTheme({
            }, semantics),
            composites: {
                border: {
                    header: stroke.thin,
                    content: stroke.thin,
                    item: stroke.thin
                },
                borderRadius: {
                    content: borderRadius.curvedLarge,
                    header: borderRadius.curvedSmall,
                    item: borderRadius.curvedSmall
                },
                tabHeaderWrapperBorder: 0,
                padding: {
                    action: gap.m,
                    header: gap.m,
                    content: gap.m
                }
            }
        },
        tabsPanelLine: {
            colors: buildTabsPanelTheme({
                activeTabItemBackground: palette.transparent,
                tabHeaderWrapperBorder: palette.neutralQuaternary,
                activeTabItemText: palette.themePrimary
            }, semantics),
            composites: {
                border: {
                    header: stroke.thin,
                    item: stroke.thick,
                    content: stroke.thin
                },
                borderRadius: {
                    header: borderRadius.curvedSmall,
                    item: borderRadius.curvedSmall,
                    content: borderRadius.curvedLarge
                },
                tabHeaderWrapperBorder: stroke.thin
            }
        }
    };
});
